import { useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import {
  ArrowLeft, Globe, MapPin, Calendar, Target, Briefcase,
  BarChart3, IndianRupee, Zap, MapPinned, Brain, Users,
  TrendingUp, Play, FileText, ExternalLink, X, Clock,
  Loader2, Linkedin, AlertTriangle,
} from "lucide-react";
import InvestorPortalDisclaimer from "@/components/investor-portal-disclaimer";
import { StructuredThesis, hasStructuredThesis } from "@/components/structured-thesis";
import { ChetanClipsGrid } from "@/components/chetan-clips-grid";

const BASE = import.meta.env.BASE_URL;

function getEmbedUrl(url: string): string | null {
  if (url.includes("shubhlabh") || url.includes("/clips/") || url.includes("/c/") || url.includes("/watch/")) {
    return url.replace(/\/(c|clips)\//, "/watch/");
  }
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
  return null;
}

const stageColors: Record<string, string> = {
  pipeline: "bg-slate-600",
  onboarding: "bg-blue-600",
  active: "bg-emerald-600",
  exited: "bg-purple-600",
};

interface ActivityEvent {
  kind: "founder_update" | "document" | "kpi" | "intel" | "system";
  occurredAt: string;
  title: string;
  detail: string | null;
  sourceTable: string;
  sourceId: number;
  isSystem: boolean;
}

const ACTIVITY_ICON: Record<ActivityEvent["kind"], string> = {
  founder_update: "📝",
  document: "📄",
  kpi: "📊",
  intel: "⚠",
  system: "•",
};

const ACTIVITY_LABEL: Record<ActivityEvent["kind"], string> = {
  founder_update: "Founder update",
  document: "Document",
  kpi: "KPI",
  intel: "Signal",
  system: "System event",
};

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function InvestorPortalCompany({ companyId }: { companyId: number }) {
  const [, navigate] = useLocation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [activeClip, setActiveClip] = useState<any>(null);
  const [activity, setActivity] = useState<ActivityEvent[] | null>(null);
  const [activityLoading, setActivityLoading] = useState(true);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const fetchCompany = useCallback(async () => {
    const token = localStorage.getItem("investor_token");
    if (!token) { navigate("/investor/login"); return; }

    try {
      const res = await fetch(`${BASE}api/investor-portal/company/${companyId}`, {
        headers: { "x-investor-token": token },
      });
      if (res.status === 401) { navigate("/investor/login"); return; }
      if (res.status === 403) { navigate("/investor/portal"); return; }
      if (res.ok) setData(await res.json());
    } catch {}
    setLoading(false);
  }, [companyId, navigate]);

  const fetchActivity = useCallback(async () => {
    const token = localStorage.getItem("investor_token");
    if (!token) return;
    setActivityLoading(true);
    try {
      const res = await fetch(`${BASE}api/investor-portal/company/${companyId}/activity?limit=100`, {
        headers: { "x-investor-token": token },
      });
      if (res.ok) {
        const json = await res.json();
        setActivity(Array.isArray(json.events) ? json.events : []);
      } else {
        setActivity([]);
      }
    } catch { setActivity([]); }
    setActivityLoading(false);
  }, [companyId]);

  useEffect(() => { fetchCompany(); }, [fetchCompany]);
  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  // Scroll to #activity if the URL hash is set on first load.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#activity") return;
    const id = setTimeout(() => {
      document.getElementById("activity")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    return () => clearTimeout(id);
  }, [loading]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 text-[#FF5C00] animate-spin" />
    </div>
  );

  if (!data) return null;

  const { company, myInvestment, founders, kpis, updates, media, documents, meeraIntel, activeRofrs } = data;

  const latestKpis = (kpis || []).reduce((acc: any, kpi: any) => {
    if (!acc[kpi.metric] || new Date(kpi.recordedAt) > new Date(acc[kpi.metric].recordedAt)) {
      acc[kpi.metric] = kpi;
    }
    return acc;
  }, {} as Record<string, any>);
  const kpiList = Object.values(latestKpis).slice(0, 6) as any[];

  return (
    <div className="space-y-8">
      {activeClip && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setActiveClip(null)}>
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setActiveClip(null)} className="absolute -top-10 right-0 text-white/60 hover:text-white">
              <X className="w-6 h-6" />
            </button>
            <div className="aspect-video bg-black rounded-xl overflow-hidden">
              <iframe src={getEmbedUrl(activeClip.url) || activeClip.url} className="w-full h-full" allow="autoplay; fullscreen" allowFullScreen />
            </div>
            <p className="text-white/60 text-sm mt-3 text-center">{activeClip.title}</p>
          </div>
        </div>
      )}

      {/* ALLOW_LOW_CONTRAST — secondary back link, intentionally muted. */}
      <button onClick={() => navigate("/investor/portal")} className="flex items-center gap-2 text-white/40 hover:text-white/70 text-sm transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Portfolio
      </button>

      {/* force-dark-surface — this hero card is hardcoded to a dark
          gradient regardless of theme. Without this class the light
          investor theme would invert text-white → near-black, leaving
          the title and value text invisible (see scripts/check-contrast.mjs). */}
      <div className="force-dark-surface relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#1A1A1A] via-[#2D1810] to-[#1A1A1A] p-8">
        {/* Subtle radial accent — kept at very low opacity so it never
            interferes with text contrast on the cards/copy above it. */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 30% 50%, #FF5C00 0%, transparent 50%)"
        }} />
        <div className="relative z-10">
          <div className="flex items-start gap-5">
            {company.logoUrl ? (
              <img src={company.logoUrl} alt="" className="w-16 h-16 rounded-xl object-cover border border-white/10" />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#FF5C00] to-[#E55200] flex items-center justify-center text-2xl font-bold font-serif text-white">
                {company.name.charAt(0)}
              </div>
            )}
            <div className="flex-1">
              <h1 className="text-3xl font-bold font-serif tracking-tight text-white drop-shadow-sm">{company.name}</h1>
              <div className="flex flex-wrap items-center gap-3 text-white/80 text-sm mt-2">
                {company.sector && <span className="flex items-center gap-1"><Target className="w-3.5 h-3.5" />{company.sector}</span>}
                {company.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{company.location}</span>}
                {company.foundedYear && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{company.foundedYear}</span>}
                {company.stage && (
                  <span className={`text-[10px] text-white px-2 py-0.5 rounded ${stageColors[company.stage] || "bg-slate-600"}`}>
                    {company.stage}
                  </span>
                )}
              </div>
            </div>
            {myInvestment?.amountInvested && (
              <div className="text-right bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                <p className="text-[10px] text-white/70 uppercase tracking-wider font-medium">My Position</p>
                <p className="text-xl font-bold text-[#FF5C00] font-mono">
                  ₹{myInvestment.amountInvested >= 100000 ? `${(myInvestment.amountInvested / 100000).toFixed(1)}L` : myInvestment.amountInvested.toLocaleString()}
                </p>
              </div>
            )}
          </div>

          {company.description && <p className="text-white/90 text-sm leading-relaxed mt-5 max-w-3xl">{company.description}</p>}

          {/* Investment Thesis — full-width structured renderer if parsed,
              otherwise fall back to the raw paragraph in a single card so
              existing companies still display something. */}
          {(company.thesis || hasStructuredThesis(company.thesisStructured)) && (
            <div className="mt-5 bg-black/30 rounded-xl p-5 border border-white/15">
              <div className="flex items-center gap-2 text-[#FF5C00] mb-3 font-semibold text-sm">
                <Briefcase className="w-4 h-4" /> Investment Thesis
              </div>
              {hasStructuredThesis(company.thesisStructured) ? (
                <StructuredThesis structured={company.thesisStructured} variant="dark" />
              ) : (
                <p className="text-white/95 text-sm leading-relaxed whitespace-pre-line">{company.thesis}</p>
              )}
            </div>
          )}

          {company.businessModel && (
            <div className="mt-3 bg-black/30 rounded-xl p-4 border border-white/15">
              <div className="flex items-center gap-2 text-[#FF5C00] mb-2 font-semibold text-sm">
                <BarChart3 className="w-4 h-4" /> Business Model
              </div>
              <p className="text-white/95 text-sm leading-relaxed">{company.businessModel}</p>
            </div>
          )}

          {company.website && (
            <a href={company.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 text-[#FF5C00] hover:underline text-sm">
              <Globe className="w-4 h-4" /> {company.website}
            </a>
          )}
        </div>
      </div>

      {activeRofrs && activeRofrs.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <p className="text-amber-400 text-sm font-medium">Active ROFR Opportunity</p>
          </div>
          {activeRofrs.map((rofr: any) => (
            <div key={rofr.id} className="text-sm text-white/70">
              <p className="font-medium text-white">{rofr.roundName}</p>
              <div className="flex items-center gap-4 mt-1 text-xs text-white/75">
                {rofr.roundSize && <span>Size: {rofr.roundSize}</span>}
                {rofr.valuation && <span>Valuation: {rofr.valuation}</span>}
                {rofr.leadInvestor && <span>Lead: {rofr.leadInvestor}</span>}
                {rofr.deadline && (
                  <span className="text-amber-400 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Deadline: {new Date(rofr.deadline).toLocaleDateString("en-IN")}
                  </span>
                )}
              </div>
              <button onClick={() => navigate("/investor/portal/rofr")} className="text-amber-400 hover:underline text-xs mt-2 inline-flex items-center gap-1">
                Go to ROFR Center <ArrowLeft className="w-3 h-3 rotate-180" />
              </button>
            </div>
          ))}
        </div>
      )}

      {meeraIntel && (meeraIntel.summary || meeraIntel.funding || meeraIntel.traction) && (
        <section className="space-y-4">
          {meeraIntel.summary && (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <div className="flex items-start gap-3">
                <Brain className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-emerald-400 text-xs uppercase tracking-wider font-medium mb-2">Investment Overview</p>
                  <p className="text-white/70 text-sm leading-relaxed">{meeraIntel.summary}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {meeraIntel.funding && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <div className="flex items-center gap-2 text-emerald-400 mb-3">
                  <IndianRupee className="w-4 h-4" />
                  <p className="text-xs uppercase tracking-wider font-medium">Funding</p>
                </div>
                {meeraIntel.funding.totalRaised && <p className="text-xl font-bold text-[#FF5C00] font-mono">{meeraIntel.funding.totalRaised}</p>}
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {meeraIntel.funding.currentRound && <span className="bg-white/10 text-white/80 text-xs px-2 py-0.5 rounded">{meeraIntel.funding.currentRound}</span>}
                  {meeraIntel.funding.valuation && <span className="text-white/75 text-xs">@ {meeraIntel.funding.valuation}</span>}
                </div>
              </div>
            )}
            {meeraIntel.traction && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <div className="flex items-center gap-2 text-emerald-400 mb-3">
                  <Zap className="w-4 h-4" />
                  <p className="text-xs uppercase tracking-wider font-medium">Traction</p>
                </div>
                <div className="space-y-1.5">
                  {meeraIntel.traction.revenue && <p className="text-sm text-white/80"><span className="text-white font-medium">Revenue:</span> {meeraIntel.traction.revenue}</p>}
                  {meeraIntel.traction.users && <p className="text-sm text-white/80"><span className="text-white font-medium">Users:</span> {meeraIntel.traction.users}</p>}
                  {meeraIntel.traction.growth && <p className="text-sm text-white/80"><span className="text-white font-medium">Growth:</span> {meeraIntel.traction.growth}</p>}
                </div>
              </div>
            )}
            {meeraIntel.geography && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <div className="flex items-center gap-2 text-emerald-400 mb-3">
                  <MapPinned className="w-4 h-4" />
                  <p className="text-xs uppercase tracking-wider font-medium">Markets</p>
                </div>
                <p className="text-sm text-white/70">{meeraIntel.geography}</p>
              </div>
            )}
          </div>

          {meeraIntel.traction?.keyMetrics && meeraIntel.traction.keyMetrics.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {meeraIntel.traction.keyMetrics.map((m: string, i: number) => (
                <div key={i} className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                  <p className="text-xs text-white/60">{m}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {meeraIntel?.financials && (
        <section>
          <h2 className="text-lg font-bold font-serif text-white mb-3 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#FF5C00]" /> Financial Overview
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <p className="text-emerald-400 text-xs uppercase tracking-wider font-medium mb-3">Key Financials</p>
              <div className="space-y-2">
                {meeraIntel.financials.revenueActual && <div className="flex justify-between text-sm"><span className="text-white/75">Revenue (Actual)</span><span className="text-white font-medium">{meeraIntel.financials.revenueActual}</span></div>}
                {meeraIntel.financials.grossMargin && <div className="flex justify-between text-sm"><span className="text-white/75">Gross Margin</span><span className="text-emerald-400 font-medium">{meeraIntel.financials.grossMargin}</span></div>}
                {meeraIntel.financials.monthlyBurn && <div className="flex justify-between text-sm"><span className="text-white/75">Monthly Burn</span><span className="text-white font-medium">{meeraIntel.financials.monthlyBurn}</span></div>}
                {meeraIntel.financials.cashRunway && <div className="flex justify-between text-sm"><span className="text-white/75">Cash Runway</span><span className="text-white font-medium">{meeraIntel.financials.cashRunway}</span></div>}
                {meeraIntel.financials.cashInBank && <div className="flex justify-between text-sm"><span className="text-white/75">Cash in Bank</span><span className="text-white font-medium">{meeraIntel.financials.cashInBank}</span></div>}
              </div>
            </div>
            {meeraIntel.fundraising && (
              <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                <p className="text-emerald-400 text-xs uppercase tracking-wider font-medium mb-3">Fundraise</p>
                <div className="space-y-2">
                  {meeraIntel.fundraising.ask && <div className="flex justify-between text-sm"><span className="text-white/75">Raising</span><span className="text-[#FF5C00] font-bold text-lg">{meeraIntel.fundraising.ask}</span></div>}
                  {meeraIntel.fundraising.desiredValuation && <div className="flex justify-between text-sm"><span className="text-white/75">Target Valuation</span><span className="text-white font-medium">{meeraIntel.fundraising.desiredValuation}</span></div>}
                </div>
              </div>
            )}
          </div>
          {meeraIntel.financials.monthlyBurn && meeraIntel.financials.cashRunway && (
            <div className="mt-3 bg-red-500/5 border border-red-500/10 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-400 text-xs font-medium mb-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Burn & Runway Monitor
              </div>
              <p className="text-white/85 text-xs">
                Monthly burn: <span className="text-white font-semibold">{meeraIntel.financials.monthlyBurn}</span> — Runway: <span className="text-white font-semibold">{meeraIntel.financials.cashRunway}</span>
              </p>
            </div>
          )}
        </section>
      )}

      {founders && founders.length > 0 && (
        <section>
          <h2 className="text-lg font-bold font-serif text-white mb-3 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#FF5C00]" /> Founding Team
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {founders.map((f: any) => (
              <div key={f.id} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF5C00] to-[#E55200] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {f.name.split(" ").map((n: string) => n[0]).join("")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white text-sm font-medium">{f.name}</p>
                  </div>
                  {f.role && <p className="text-[#FF5C00] text-xs">{f.role}</p>}
                  {f.bio && <p className="text-white/75 text-xs mt-1 leading-relaxed">{f.bio}</p>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {kpiList.length > 0 && (
        <section>
          <h2 className="text-lg font-bold font-serif text-white mb-3 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#FF5C00]" /> Traction Snapshot
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {kpiList.map((kpi: any) => (
              <div key={kpi.id} className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                <p className="text-xl font-bold text-[#FF5C00] font-mono">{kpi.value}</p>
                <p className="text-white/75 text-xs mt-1">{kpi.metric}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <ChetanClipsGrid endpoint={`api/public/companies/${companyId}/clips`} tone="light" />
      </section>

      {media && media.length > 0 && (
        <section>
          <h2 className="text-lg font-bold font-serif text-white mb-3 flex items-center gap-2">
            <Play className="w-5 h-5 text-[#FF5C00]" /> Media & Coverage
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {media.map((m: any) => {
              const embeddable = m.type === "clip" || m.type === "video" || getEmbedUrl(m.url);
              return (
                <div
                  key={m.id}
                  onClick={() => embeddable && m.url ? setActiveClip(m) : m.url && window.open(m.url, "_blank", "noopener,noreferrer")}
                  className={`bg-white/5 border border-white/10 rounded-xl p-4 ${m.url ? "cursor-pointer hover:border-[#FF5C00]/30" : ""}`}
                >
                  <div className="aspect-video bg-white/5 rounded-lg flex items-center justify-center mb-3">
                    {m.thumbnailUrl ? (
                      <img src={m.thumbnailUrl} alt="" className="w-full h-full object-cover rounded-lg" />
                    ) : embeddable ? (
                      <Play className="w-8 h-8 text-[#FF5C00]" />
                    ) : (
                      // ALLOW_LOW_CONTRAST — decorative icon for empty thumbnail
                      <FileText className="w-6 h-6 text-white/20" />
                    )}
                  </div>
                  <p className="text-white text-sm font-medium truncate">{m.title}</p>
                  {m.description && <p className="text-white/75 text-xs mt-0.5 line-clamp-2">{m.description}</p>}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section id="activity" className="scroll-mt-20">
        <h2 className="text-lg font-bold font-serif text-white mb-3 flex items-center gap-2">
          <Clock className="w-5 h-5 text-[#FF5C00]" /> Activity
        </h2>
        {activityLoading ? (
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-white/75 text-sm flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading activity…
          </div>
        ) : !activity || activity.length === 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-white/75 text-sm">
            No activity yet for this company.
          </div>
        ) : (
          <div className="space-y-2">
            {activity.map((e) => {
              const key = `${e.sourceTable}:${e.sourceId}`;
              const expanded = expandedKey === key;
              const hasDetail = !!e.detail && e.detail.trim().length > 0;
              return (
                <div key={key} className="bg-white/5 border border-white/10 rounded-lg">
                  <button
                    type="button"
                    onClick={() => hasDetail && setExpandedKey(expanded ? null : key)}
                    className={`w-full text-left p-4 flex items-start gap-3 ${hasDetail ? "hover:bg-white/[0.07] cursor-pointer" : "cursor-default"}`}
                  >
                    <span className="text-lg leading-none mt-0.5 flex-shrink-0">{ACTIVITY_ICON[e.kind]}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-white/70 font-medium mb-1">
                        <span>{ACTIVITY_LABEL[e.kind]}</span>
                        <span>·</span>
                        <span>{fmtDate(e.occurredAt)}</span>
                      </div>
                      <p className="text-white/80 text-sm">{e.title}</p>
                      {expanded && hasDetail && (
                        <p className="text-white/60 text-sm mt-3 whitespace-pre-wrap">{e.detail}</p>
                      )}
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {updates && updates.length > 0 && (
        <section>
          <h2 className="text-lg font-bold font-serif text-white mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#FF5C00]" /> Recent Updates
          </h2>
          <div className="space-y-3">
            {updates.map((u: any) => (
              <div key={u.id} className="bg-white/5 border border-white/10 rounded-xl p-5">
                <p className="text-[#FF5C00] text-xs font-medium mb-2">
                  Week of {new Date(u.weekOf).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </p>
                {u.highlights && (
                  <div className="mb-2">
                    <p className="text-white/70 text-[10px] uppercase tracking-wider font-medium mb-1">Highlights</p>
                    <p className="text-white/90 text-sm">{u.highlights}</p>
                  </div>
                )}
                {u.challenges && (
                  <div className="mb-2">
                    <p className="text-white/70 text-[10px] uppercase tracking-wider font-medium mb-1">Challenges</p>
                    <p className="text-white/90 text-sm">{u.challenges}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {documents && documents.length > 0 && (
        <section>
          <h2 className="text-lg font-bold font-serif text-white mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#FF5C00]" /> Documents
          </h2>
          <div className="space-y-2">
            {documents.map((d: any) => (
              <a key={d.id} href={d.fileUrl || d.externalUrl || "#"} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg p-4 hover:border-white/20 transition-colors">
                <div className="flex items-center gap-3">
                  {/* ALLOW_LOW_CONTRAST — decorative file icon */}
                <FileText className="w-4 h-4 text-white/30" />
                  <p className="text-white text-sm">{d.title}</p>
                </div>
                {/* ALLOW_LOW_CONTRAST — decorative external link icon */}
                <ExternalLink className="w-4 h-4 text-white/20" />
              </a>
            ))}
          </div>
        </section>
      )}

      <InvestorPortalDisclaimer />
    </div>
  );
}
